import { z } from "zod";
/** Scale questions shared across kinds keep their id, so a coach can compare answers over time. */
const SCALES = {
    confidence: {
        id: "confidence",
        kind: "scale",
        prompt: "How sure are you that you can make this change?",
        low: "Not sure at all",
        high: "Very sure",
    },
    energy: {
        id: "energy",
        kind: "scale",
        prompt: "Over the last two weeks, how has your energy been?",
        low: "Very low",
        high: "Very high",
    },
    stress: {
        id: "stress",
        kind: "scale",
        prompt: "Over the last two weeks, how stressed have you felt?",
        low: "Not at all",
        high: "Very stressed",
    },
    sleep: {
        id: "sleep",
        kind: "scale",
        prompt: "Over the last two weeks, how well have you slept?",
        low: "Very badly",
        high: "Very well",
    },
};
export const COACHING_ASSESSMENTS = {
    intake: {
        title: "Getting started",
        intro: "A few questions so your coach can get to know you. It takes about three minutes.",
        questions: [
            { id: "focus", kind: "text", prompt: "What would you most like to change through coaching?" },
            { id: "why_now", kind: "text", prompt: "Why does this matter to you now?" },
            SCALES.confidence,
            SCALES.energy,
            SCALES.stress,
            SCALES.sleep,
            {
                id: "anything_else",
                kind: "text",
                prompt: "Is there anything your coach should know before you meet?",
                optional: true,
            },
        ],
    },
    progress: {
        title: "How it is going",
        intro: "A short check on how things have changed since you started. It takes about two minutes.",
        questions: [
            {
                id: "progress",
                kind: "scale",
                prompt: "How much progress have you made towards your goal?",
                low: "None yet",
                high: "A lot",
            },
            SCALES.confidence,
            SCALES.energy,
            SCALES.stress,
            SCALES.sleep,
            { id: "went_well", kind: "text", prompt: "What has gone well?" },
            {
                id: "next_focus",
                kind: "text",
                prompt: "What would you like to work on next?",
                optional: true,
            },
        ],
    },
    check_in: {
        title: "Daily check-in",
        intro: "How today is going with your medicine. It takes under a minute.",
        for: "care_team",
        selfStart: true,
        questions: [
            {
                id: "confidence",
                kind: "scale",
                prompt: "How confident do you feel managing your medicine today?",
                low: "Not confident",
                high: "Very confident",
            },
            {
                id: "concern",
                kind: "choice",
                prompt: "Is anything on your mind today?",
                multiple: false,
                options: [
                    { id: "nothing", label: "Nothing in particular" },
                    { id: "remembering", label: "Remembering my doses" },
                    { id: "side_effects", label: "Side effects" },
                    { id: "refill", label: "Running out or a refill" },
                    { id: "bleeding", label: "Bruising or bleeding", urgent: true },
                    { id: "fall", label: "A fall or a knock to the head", urgent: true },
                ],
            },
            {
                id: "note",
                kind: "text",
                prompt: "Anything you want your care team to know?",
                optional: true,
            },
        ],
    },
    symptoms: {
        title: "Log a symptom",
        intro: "What you noticed, so your care team can see it. Logging it here does not alert anyone in an emergency.",
        for: "care_team",
        selfStart: true,
        questions: [
            {
                id: "symptoms",
                kind: "choice",
                prompt: "What have you noticed?",
                multiple: true,
                options: [
                    { id: "bruising", label: "Bruising", urgent: true },
                    { id: "bleeding", label: "Nose or gum bleeding", urgent: true },
                    { id: "headache", label: "Headache" },
                    { id: "dizziness", label: "Dizziness or weakness" },
                    { id: "pain", label: "Pain or swelling" },
                    { id: "other", label: "Something else" },
                ],
            },
            { id: "note", kind: "text", prompt: "Tell your care team more", optional: true },
        ],
    },
};
export const CoachingAssessmentKind = z.enum(["intake", "progress", "check_in", "symptoms"]);
export const SCALE_POINTS = [1, 2, 3, 4, 5];
/** Answers that fit the kind's questions: every required one answered, scales on the five-point range, choices from the list. */
export function assessmentAnswers(kind) {
    const shape = {};
    for (const q of COACHING_ASSESSMENTS[kind].questions) {
        if (q.kind === "choice") {
            const option = z.enum(q.options.map((o) => o.id));
            shape[q.id] = q.multiple ? z.array(option).min(1).max(q.options.length) : option;
            continue;
        }
        const field = q.kind === "scale" ? z.number().int().min(1).max(5) : z.string().trim().min(1).max(2000);
        shape[q.id] = q.kind === "text" && q.optional ? field.optional() : field;
    }
    return z.object(shape).strict();
}
/** The questionnaires a relationship offers: every shared one, and those for its kind. */
export function assessmentKindsFor(kind) {
    return CoachingAssessmentKind.options.filter((k) => {
        const spec = COACHING_ASSESSMENTS[k];
        return !spec.for || spec.for === kind;
    });
}
/** Whether the member may start this questionnaire themselves. */
export const selfStarts = (kind) => COACHING_ASSESSMENTS[kind].selfStart === true;
/** Whether an answer picked something that should reach the clinician straight away. */
export function urgentAnswers(kind, answers) {
    if (!answers)
        return [];
    const out = [];
    for (const q of COACHING_ASSESSMENTS[kind].questions) {
        if (q.kind !== "choice")
            continue;
        const picked = [].concat(answers[q.id] ?? []);
        for (const o of q.options)
            if (o.urgent && picked.includes(o.id))
                out.push(o.label);
    }
    return out;
}
//# sourceMappingURL=coaching-assessments.js.map